import { actions as filterActions } from '@/state/filterActions';
import { actions as hotelPageActions } from '@/state/hotelPageActions';
import { wrapper } from '@/state/store';
import { browserQueryParamsToHotelPageFilters } from '@/utils/url/hotelPageQueryParams';
import { getHotel } from '@/utils/fetch/kayak/kayakFetcher';
import { getExtraHotelData } from '@/utils/fetch/extraHotelDataFetcher';
import { buildCommonProps, sanitizeCommonProps } from '@/server/middleware';
import { createHeaders } from '../resultat/[...destination]';
import { queryParamToString } from 'shared/util/query';

import type { ExtraHotelData } from '@/server/integration/extraHotelDataResponseTypes';
import { ErrorPage } from '@/scenes/errorPage/ErrorPage';
import HotelPage from '@/scenes/hotelPage/HotelPage';
import { getKayakHotelId } from '@/server/datafeed/kayakHotelId';

interface Props {
    statusCode: number | null;
    extraHotelData?: ExtraHotelData;
}

const Hotel = (props: Props) => {
    wrapper.useHydration(props);
    const { statusCode, extraHotelData } = props;
    if (statusCode) {
        return <ErrorPage statusCode={statusCode} />;
    }
    return <HotelPage extraHotelData={extraHotelData} />;
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ req, res, query }) => {
    let statusCode: number | null = null;

    const commonProps = await buildCommonProps(req, res, query);

    const filters = browserQueryParamsToHotelPageFilters(query);

    store.dispatch(filterActions.setFilters(filters));

    const hotelId = queryParamToString(query.hotel);

    // Checks if hotelId is not a number...
    if (!/^\d+$/.test(hotelId)) {
        return getKayakHotelId(hotelId).then((newId) => {
            if (newId) {
                return {
                    redirect: {
                        destination: `/detaljer/${newId}`,
                        permanent: false,
                    },
                };
            }
            return {
                redirect: {
                    destination: '/error',
                    permanent: false,
                },
            };
        });
    }

    const [hotel, extraHotelData] = await Promise.all([
        getHotel(store.getState().filters, createHeaders(req, res)),
        getExtraHotelData([filters.hotel], createHeaders(req, res)),
    ]);

    if (hotel.status === 200) {
        store.dispatch(hotelPageActions.setHotel(hotel.data));
    } else {
        store.dispatch(hotelPageActions.setHotelError());
        res.statusCode = statusCode = hotel.status ?? null;
    }
    return {
        notFound: statusCode === 404,
        props: {
            statusCode,
            extraHotelData: extraHotelData.data?.[filters.hotel.toLowerCase()] || null,
            ...sanitizeCommonProps(commonProps),
        },
    };
});

export default Hotel;
